// Loads the 3-word facial-cue pill library (public/face_pills.json, sourced
// agentically from lietome.ch + the fringe micro-expression canon) and resolves
// a blendshape base name to its pill. This is a NOVELTY reading aid, not real
// lie detection (micro-expression "tells" are contested and unreliable).

export type PillTone = "tell" | "deception" | "positive" | "negative" | "stress" | "neutral";

export interface FacePill {
  pill: string; // exactly three words
  tone: PillTone;
  cue: string;
}

export interface GenericPill {
  pill: string;
  cue: string;
}

let byAction = new Map<string, FacePill>();
let generic: GenericPill[] = [];
let disclaimer = "";

/** Load the pill library once. Safe before/without the file (404 -> no pills). */
export async function loadFacePills(): Promise<number> {
  try {
    const res = await fetch("/face_pills.json", { cache: "force-cache" });
    if (!res.ok) return 0;
    const data = await res.json();
    disclaimer = String(data?.disclaimer ?? "");
    const map = new Map<string, FacePill>();
    for (const [k, v] of Object.entries(data?.pills ?? {})) {
      const p = v as Partial<FacePill>;
      if (p && typeof p.pill === "string") {
        map.set(k, { pill: p.pill, tone: (p.tone as PillTone) ?? "neutral", cue: p.cue ?? "" });
      }
    }
    byAction = map;
    generic = Array.isArray(data?.generic) ? (data.generic as GenericPill[]) : [];
    return byAction.size;
  } catch {
    return 0;
  }
}

/** Resolve a facial-action base name to its pill, or null if unmapped/unloaded. */
export function pillFor(actionName: string): FacePill | null {
  return byAction.get(actionName) ?? null;
}

export function genericPills(): GenericPill[] {
  return generic;
}

export function facePillsDisclaimer(): string {
  return disclaimer;
}

/** HUD colour for a pill tone. */
export function toneColor(tone: PillTone): string {
  switch (tone) {
    case "positive":
      return "#39ffa0";
    case "deception":
      return "#ff2f4a";
    case "negative":
      return "#ff8a3d";
    case "stress":
      return "#ffd23d";
    case "tell":
      return "#b96bff";
    default:
      return "#39d7ff";
  }
}
